import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PlayerDemo from './components/PlayerDemo';
import VideoCard from '@/components/feature/VideoCard';
import {
  formatDate,
  formatViews,
  getCategoryName,
  getRelatedVideos,
  getVideoById,
} from '@/utils/video';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  const related = useMemo(() => (video ? getRelatedVideos(video) : []), [video]);

  useEffect(() => {
    setShareOpen(false);
    if (video) {
      document.title = `${video.title} - 宅男视频`;
    } else {
      document.title = '视频未找到 - 宅男视频';
    }
  }, [video]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!video) {
    return (
      <div className="w-full px-4 md:px-6 py-16 md:py-24">
        <div className="mx-auto max-w-lg text-center">
          <span className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-accent-100 text-accent-600">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 text-xl md:text-2xl font-bold text-foreground-950">视频不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的视频可能已调整，去首页看看其他精彩内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const shareTargets = [
    { label: '分享到微博', icon: 'ri-weibo-fill' },
    { label: '分享到 B 站', icon: 'ri-bilibili-fill' },
    { label: '复制链接', icon: 'ri-link' },
  ];

  const handleShareAction = (label: string) => {
    setShareOpen(false);
    if (label === '复制链接' && navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}${__BASE_PATH__}/detail/${video.id}`).catch(() => undefined);
    }
    setToast(`${label}（演示）`);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <nav className="flex items-center flex-wrap gap-2 text-xs text-primary-200 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${video.category}`} className="hover:text-accent-400 transition-colors cursor-pointer">
              {getCategoryName(video.category)}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50 line-clamp-1">{video.title}</span>
          </nav>
          <div className="mx-auto max-w-4xl">
            <PlayerDemo video={video} />
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-6">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 leading-snug">{video.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">
                  <i className="ri-price-tag-3-line"></i>
                  {getCategoryName(video.category)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {video.author}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(video.publishedAt)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-play-circle-line"></i>
                  {formatViews(video.views)} 次播放
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
                <button
                  type="button"
                  onClick={() => setToast('已加入收藏（演示，无实际保存）')}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
                <Link
                  to={`/category/${video.category}`}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{getCategoryName(video.category)}
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200">
                <h2 className="text-base font-bold text-foreground-950 mb-3">内容简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
              </div>

              <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200 p-4">
                <h3 className="flex items-center gap-2 text-sm font-bold text-accent-800 mb-2">
                  <i className="ri-lightbulb-flash-line"></i>
                  推荐理由
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{video.recommendReason}</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-600 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-4">
                <i className="ri-movie-line text-accent-500"></i>
                相关推荐
              </h2>
              <div className="flex flex-col gap-3">
                {related.map((item) => (
                  <VideoCard key={item.id} video={item} variant="list" />
                ))}
              </div>
              {related.length === 0 && (
                <p className="text-sm text-foreground-500 py-4 text-center">暂无相关推荐</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/60"
            onClick={() => setShareOpen(false)}
            role="presentation"
          ></div>
          <div className="relative w-full max-w-sm rounded-lg bg-background-50 border border-background-200 p-5 animate-scale-in">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-bold text-foreground-950">分享视频</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-4">分享功能为演示状态，不会产生真实分享。</p>
            <div className="flex flex-col gap-2">
              {shareTargets.map((target) => (
                <button
                  key={target.label}
                  type="button"
                  onClick={() => handleShareAction(target.label)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  <i className={`${target.icon} text-lg`}></i>
                  {target.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}